import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import User from './User';
import { EntryProductModel } from '../../types/EntryProduct';
import {
    InventoryProductModel,
    InventoryProductPayload,
} from '../../types/InventoryProduct';
import {
    ProductGroup,
    ProductModel,
    ProductPayload,
    ProductType,
} from '../../types/Products';
import { RegionModel } from '../../types/Region';
import { ProductDBServiceOptions } from '../../types/dbService';
import { ProductsFilterOptions } from '../../types/productFilters';
import {
    applyProductsFiltersQueries,
    getProductsByGroupsQuery,
    getProductsSortQuery,
    getUnsafeProductsSearchQueries,
} from '../utils';
import { getDuplicatesFreeArrayByKey } from '../../utils/array';
import { FILTER_TYPE } from 'shared/types/filter';
import {
    addPreparedDataBatch,
    mapRecord,
    PreparedProductData,
} from './helpers/product/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddProductActions,
    prepareCreateProduct,
    prepareProductInventoryRecords,
} from './helpers/product/productAdd';
import { InventoryChangePayload } from '../../types/InventoryChange';

class Product {
    private database: Database;
    private collection: Collection<ProductModel>;
    private table = 'products';
    private options: ProductDBServiceOptions;

    constructor(options: ProductDBServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    countAll() {
        return this.collection.query().fetchCount();
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getByIDs(ids: string[]) {
        if (!ids.length) {
            return new Promise<ProductModel[]>((resolve) => resolve([]));
        }

        return this.collection
            .query(Q.or(...ids.map((id) => Q.where('id', id))))
            .fetch();
    }

    async getAllBrands(type: ProductType) {
        const typeProducts = await this.collection
            .query(Q.where('product_type', type), Q.sortBy('brand'))
            .fetch();

        const brands = getDuplicatesFreeArrayByKey(typeProducts, 'brand');
        return brands.map((prod) => prod.brand || '');
    }

    getFilterProductsSearchLokiQuery(searchText: string) {
        const unsafeSearchQueries = getUnsafeProductsSearchQueries(searchText);

        // @ts-ignore
        return Q.unsafeLokiTransform((raws, loki) => {
            let result = raws;

            unsafeSearchQueries.forEach(
                (query) => (result = query(result, loki)),
            );

            return result;
        });
    }

    getFilterProductsSearchSQLQuery(searchText: string) {
        const parsedSearchText = searchText.toLowerCase().trim();

        const sanitizedSearch = Q.sanitizeLikeString(parsedSearchText);

        if (!sanitizedSearch.length) return;

        const SQLiteProductQuery = `brand || iif(family IS NULL, '', ' ') || ifnull(family, '') || iif(material IS NULL, '', ' ') || ifnull(material, '') || iif(shape IS NULL, '', ' ') || ifnull(shape, '') || iif(clips IS NULL, '', ' ') || ifnull(clips, '') LIKE`;

        const queries = [
            Q.unsafeSqlExpr(`${SQLiteProductQuery} '${sanitizedSearch}%'`),
            Q.unsafeSqlExpr(`${SQLiteProductQuery} '% ${sanitizedSearch}%'`),
            Q.where('name', Q.like(`${sanitizedSearch}%`)),
            Q.where('name', Q.like(`% ${sanitizedSearch}%`)),
        ];

        return Q.or(
            // @ts-ignore
            ...queries,
        );
    }

    async getFilteredProducts({
        searchText,
        filters,
        filterType,
        userId,
    }: ProductsFilterOptions) {
        const queries: Q.Clause[] = [];

        if (filters) {
            const filterQueries = await applyProductsFiltersQueries(
                filters,
                this.options,
            );
            filterQueries.forEach((filterQuery) => queries.push(filterQuery));
        }

        const searchQuery =
            filterType === FILTER_TYPE.LOKI
                ? this.getFilterProductsSearchLokiQuery(searchText)
                : this.getFilterProductsSearchSQLQuery(searchText);

        if (searchQuery) {
            queries.push(searchQuery);
        }

        const userService = new User({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const userRegion = await userService.getUserRegion(userId);

        // MSTD-8086: getUserRegion() can return undefined (e.g. before the
        // user row is synced); skip the region-scoped join in that case
        // instead of dereferencing userRegion.id.
        const productsInUserRegionQueries = userRegion
            ? [
                  Q.experimentalJoinTables(['product_sales_units']),
                  Q.experimentalNestedJoin(
                      'product_sales_units',
                      'product_sales_unit_regions',
                  ),
                  // MSTD-7656: custom products have no product_sales_units /
                  // product_sales_unit_regions rows, so the region join would
                  // filter them out entirely. Let them bypass the region check.
                  Q.or(
                      Q.where('product_type', Q.eq('custom')),
                      Q.on(
                          'product_sales_units',
                          Q.on(
                              'product_sales_unit_regions',
                              Q.where('region_id', Q.eq(userRegion.id)),
                          ),
                      ),
                  ),
              ]
            : [];

        const availableProductsQuery = [
            Q.or(
                Q.where('not_available', null),
                Q.where('not_available', false),
            ),
        ];

        const products = await this.collection
            .query(
                ...queries,
                ...productsInUserRegionQueries,
                ...availableProductsQuery,
                ...getProductsSortQuery(),
            )
            .fetch();

        return products;
    }

    getByGroups(groups: ProductGroup[]) {
        return this.collection
            .query(getProductsByGroupsQuery(groups), ...getProductsSortQuery())
            .fetch();
    }

    async isProductFromUserRegion(
        product: ProductModel,
        regions: Collection<RegionModel>,
        userRegion: RegionModel | undefined,
    ) {
        // MSTD-8086: without a resolved region we can't confirm membership.
        if (!userRegion) return false;

        const productSalesUnits = await product?.productSalesUnits;

        if (productSalesUnits && productSalesUnits.length !== 0) {
            for (const productSalesUnit of productSalesUnits) {
                const unitRegions = await regions
                    .query(
                        Q.on(
                            'product_sales_unit_regions',
                            Q.where(
                                'product_sales_unit_id',
                                productSalesUnit.id,
                            ),
                        ),
                    )
                    .fetch();

                if (
                    unitRegions.length !== 0 &&
                    unitRegions.some(
                        (unitRegion) => unitRegion.id === userRegion.id,
                    )
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    async getProductsBasedOnUserRegionAndProductType(
        products:
            | EntryProductModel[]
            | InventoryProductModel[]
            | ProductModel[],
        type: string | null,
        userId: string,
    ): Promise<ProductModel[]> {
        const productIds = (products ?? [])
            .map(
                (prod) =>
                    (prod as EntryProductModel | InventoryProductModel)
                        .productId ?? prod.id,
            )
            .filter((id): id is string => !!id);

        if (!productIds.length) {
            return [];
        }

        const typeQuery = type ? [Q.where('product_type', type)] : [];

        if (type === ProductType.custom) {
            return this.collection
                .query(Q.where('id', Q.oneOf(productIds)), ...typeQuery)
                .fetch();
        }

        const userService = new User({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const userRegion = await userService.getUserRegion(userId);

        if (!userRegion) {
            return [];
        }

        return this.collection
            .query(
                Q.where('id', Q.oneOf(productIds)),
                ...typeQuery,
                Q.experimentalJoinTables(['product_sales_units']),
                Q.experimentalNestedJoin(
                    'product_sales_units',
                    'product_sales_unit_regions',
                ),
                Q.on(
                    'product_sales_units',
                    Q.on(
                        'product_sales_unit_regions',
                        Q.where('region_id', Q.eq(userRegion.id)),
                    ),
                ),
            )
            .fetch();
    }

    async add(
        payload: ProductPayload,
        userId: string,
        postPayload?: {
            inventoryProduct?: Omit<InventoryProductPayload, 'productId'>;
            inventoryChange?: Omit<
                InventoryChangePayload,
                'inventoryProductId'
            >;
        },
    ) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logger.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        return await this.database.write(async () => {
            const preparedData: PreparedProductData[] = [];
            const createdProduct = prepareCreateProduct({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });
            preparedData.push(mapRecord(createdProduct, 'products', 'add'));

            const { inventoryProduct, inventoryChange } =
                prepareProductInventoryRecords({
                    inventoryProductPayload: postPayload?.inventoryProduct,
                    inventoryChangePayload: postPayload?.inventoryChange,
                    product: createdProduct,
                    userId,
                    organisationID,
                    database: this.database,
                });
            if (inventoryProduct) {
                preparedData.push(
                    mapRecord(inventoryProduct, 'inventory_products', 'add'),
                );
            }
            if (inventoryChange) {
                preparedData.push(
                    mapRecord(inventoryChange, 'inventory_changes', 'add'),
                );
            }

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logAddProductActions({
                product: createdProduct,
                inventoryProduct,
                inventoryChange,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return {
                createdProduct,
                createdInventoryProduct: inventoryProduct,
            };
        });
    }
}

export default Product;
